/* eslint-disable */
import { getLocale, experimentalStaticLocale } from '../runtime.js';

/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

/** @typedef {{ operator: NonNullable<unknown>, address: NonNullable<unknown>, registration: NonNullable<unknown> }} Legal_Dpa_BodyInputs */

const en_legal_dpa_body = /** @type {(inputs: Legal_Dpa_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`This agreement is part of the [business terms](/terms) and applies when we handle your clients' personal data for you, as GDPR Article 28 requires. It takes effect when you accept those terms and lasts as long as we handle that data.

## 1. Parties and roles

The **business** using Complete Athlete is the controller. ${i?.operator}, ${i?.address}, ${i?.registration} is the processor ("we"). Data an athlete keeps in their own account, and our own account data about your team, are outside this agreement: for those we are the controller, under our [Privacy Policy](/privacy).

## 2. What we process

- **Subject:** providing scheduling, client records, plans, payments, invoicing and related features.
- **People:** your clients and prospects, guests who book, and your team.
- **Data:** names, contact details, booking and payment history, invoice details, notes, plans, intake answers, and files you upload.
- **Special categories:** health data you or your clients enter, such as injuries or intake answers.
- **Actions:** storing, displaying, organising, sending notifications, and deleting.
- **Duration:** for as long as you use the service, plus the return and deletion period in section 8.

## 3. Your instructions

We process this data only on your documented instructions, which are these terms and your actions in the app, unless the law requires otherwise. If so, we will tell you first, unless the law forbids it. We will tell you if we think an instruction breaks data protection law.

## 4. Your responsibilities

You must have a legal basis, including explicit consent where needed for health data, give your clients a privacy notice, and put only lawful, necessary data in the app.

## 5. Confidentiality and security

Anyone we authorise to process this data is bound to keep it confidential. We use encrypted connections, hashed passwords, role-based access for your team, scoped API keys and error monitoring, and will maintain measures appropriate to the risk.

## 6. Sub-processors

You authorise these sub-processors: Railway (hosting and storage), Stripe (payments), Resend (email), SMSAPI (text messages), Anthropic (AI features, where used), Google (address geocoding), PostHog (usage analytics), Sentry (error reports).

We will give at least 30 days' notice of a new one, by email or in the app. You can object on reasonable data protection grounds, and if we cannot resolve it, you can end the contract. We bind each sub-processor to equivalent duties and remain responsible for them.

Your own Stripe connected account is your contract with Stripe, not a sub-processing arrangement of ours.

## 7. Helping you, and breaches

We will help you, taking into account what we know and can do, to answer requests from individuals, keep data secure, carry out impact assessments and consult authorities. If someone contacts us directly about your data, we will pass the request on.

We will notify you of a personal data breach without undue delay, and within **48 hours** of becoming aware of it wherever we can, with what you need to meet your own duties.

## 8. Return and deletion

When you leave, you can ask for an export of your data. We then delete it, including from active systems, within **30 days**, and from backups as they expire, unless the law requires us to keep it.

## 9. Transfers

Where a sub-processor processes data outside the European Economic Area, we rely on an adequacy decision or the European Commission's standard contractual clauses.

## 10. Audits

We will give you the information you need to show compliance. You can audit us, or appoint an independent auditor, on 30 days' notice, during business hours, no more than once a year unless a breach or an authority requires otherwise, and under confidentiality.

## 11. Liability and conflicts

The liability provisions of the business terms apply to the extent the law allows. If this agreement conflicts with those terms on data protection, this agreement prevails.`)
};

const pl_legal_dpa_body = /** @type {(inputs: Legal_Dpa_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`Niniejsza umowa jest częścią [Regulaminu dla firm](/terms) i ma zastosowanie, gdy przetwarzamy dla Ciebie dane osobowe Twoich podopiecznych, zgodnie z wymogami art. 28 RODO. Wchodzi w życie z chwilą akceptacji tego Regulaminu i obowiązuje tak długo, jak przetwarzamy te dane.

## 1. Strony i role

**Firma** korzystająca z Complete Athlete jest administratorem. ${i?.operator}, ${i?.address}, ${i?.registration} jest podmiotem przetwarzającym („my”). Dane, które sportowiec przechowuje na własnym koncie, oraz nasze własne dane kont dotyczące Twojego zespołu nie są objęte tą umową: w ich przypadku jesteśmy administratorem zgodnie z naszą [Polityką prywatności](/privacy).

## 2. Co przetwarzamy

- **Przedmiot:** zapewnianie grafiku, kartotek podopiecznych, planów, płatności, fakturowania i powiązanych funkcji.
- **Osoby:** Twoi podopieczni i potencjalni podopieczni, goście dokonujący rezerwacji oraz Twój zespół.
- **Dane:** imiona i nazwiska, dane kontaktowe, historia rezerwacji i płatności, dane do faktur, notatki, plany, odpowiedzi z ankiety wstępnej oraz przesyłane przez Ciebie pliki.
- **Szczególne kategorie danych:** dane dotyczące zdrowia wprowadzane przez Ciebie lub Twoich podopiecznych, takie jak kontuzje lub odpowiedzi z ankiety wstępnej.
- **Czynności:** przechowywanie, wyświetlanie, porządkowanie, wysyłanie powiadomień i usuwanie.
- **Okres:** tak długo, jak korzystasz z usługi, oraz przez okres zwrotu i usunięcia danych opisany w sekcji 8.

## 3. Twoje polecenia

Przetwarzamy te dane wyłącznie na Twoje udokumentowane polecenia, którymi są niniejsze warunki i Twoje działania w aplikacji, chyba że prawo stanowi inaczej. W takim przypadku najpierw Cię o tym poinformujemy, chyba że prawo tego zabrania. Poinformujemy Cię, jeśli uznamy, że polecenie narusza przepisy o ochronie danych.

## 4. Twoje obowiązki

Musisz mieć podstawę prawną, w tym wyraźną zgodę, gdy jest potrzebna w przypadku danych dotyczących zdrowia, przekazać podopiecznym informację o prywatności i wprowadzać do aplikacji wyłącznie dane zgodne z prawem i niezbędne.

## 5. Poufność i bezpieczeństwo

Każda osoba, którą upoważniamy do przetwarzania tych danych, jest zobowiązana do zachowania ich w poufności. Stosujemy szyfrowane połączenia, hasła w postaci skrótu, dostęp oparty na rolach dla Twojego zespołu, klucze API o ograniczonym zakresie i monitorowanie błędów oraz będziemy utrzymywać środki odpowiednie do ryzyka.

## 6. Podwykonawcy przetwarzania

Upoważniasz nas do korzystania z następujących podwykonawców przetwarzania (dalszych podmiotów przetwarzających): Railway (hosting i przechowywanie), Stripe (płatności), Resend (e-mail), SMSAPI (SMS-y), Anthropic (funkcje AI, jeśli są używane), Google (geokodowanie adresów), PostHog (analityka korzystania), Sentry (raporty o błędach).

O nowym podwykonawcy poinformujemy Cię co najmniej 30 dni wcześniej, e-mailem lub w aplikacji. Możesz zgłosić sprzeciw z uzasadnionych powodów związanych z ochroną danych, a jeśli nie zdołamy rozwiązać sprawy, możesz zakończyć umowę. Każdego podwykonawcę zobowiązujemy do przestrzegania równoważnych obowiązków i odpowiadamy za niego.

Twoje własne połączone konto Stripe działa na podstawie Twojej umowy ze Stripe i nie stanowi naszego powierzenia dalszego przetwarzania.

## 7. Pomoc dla Ciebie i naruszenia

Uwzględniając naszą wiedzę i możliwości, pomożemy Ci odpowiadać na żądania osób, zapewniać bezpieczeństwo danych, przeprowadzać oceny skutków dla ochrony danych i konsultować się z organami. Jeśli ktoś skontaktuje się z nami bezpośrednio w sprawie Twoich danych, przekażemy Ci to żądanie.

O naruszeniu ochrony danych osobowych poinformujemy Cię bez zbędnej zwłoki i, gdy tylko jest to możliwe, w ciągu **48 godzin** od stwierdzenia naruszenia, przekazując informacje potrzebne do wykonania Twoich własnych obowiązków.

## 8. Zwrot i usunięcie danych

Gdy odchodzisz, możesz poprosić o eksport swoich danych. Następnie usuwamy je, także z aktywnych systemów, w ciągu **30 dni**, a z kopii zapasowych w miarę ich wygasania, chyba że prawo wymaga od nas ich zachowania.

## 9. Przekazywanie danych

Gdy podwykonawca przetwarzania przetwarza dane poza Europejskim Obszarem Gospodarczym, opieramy się na decyzji stwierdzającej odpowiedni stopień ochrony lub na standardowych klauzulach umownych Komisji Europejskiej.

## 10. Audyty

Przekażemy Ci informacje potrzebne do wykazania zgodności. Możesz przeprowadzić u nas audyt lub wyznaczyć niezależnego audytora, z 30-dniowym wyprzedzeniem, w godzinach pracy, nie częściej niż raz w roku, chyba że naruszenie lub organ wymaga inaczej, i z zachowaniem poufności.

## 11. Odpowiedzialność i sprzeczności

Postanowienia Regulaminu dla firm dotyczące odpowiedzialności mają zastosowanie w zakresie dozwolonym przez prawo. Jeśli niniejsza umowa jest sprzeczna z tym Regulaminem w zakresie ochrony danych, pierwszeństwo ma niniejsza umowa.`)
};

/**
* | output |
* | --- |
* | "This agreement is part of the [business terms](/terms) and applies when we handle your clients' personal data for you, as GDPR Article 28 requires. It takes ..." |
*
* @param {Legal_Dpa_BodyInputs} inputs
* @param {{ locale?: "en" | "pl" }} options
* @returns {LocalizedString}
*/
export const legal_dpa_body = /** @type {((inputs: Legal_Dpa_BodyInputs, options?: { locale?: "en" | "pl" }) => LocalizedString) & import('../runtime.js').MessageMetadata<Legal_Dpa_BodyInputs, { locale?: "en" | "pl" }, {}>} */ ((inputs, options = {}) => {
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	if (locale === "pl") return pl_legal_dpa_body(inputs)
	return en_legal_dpa_body(inputs)
});